import React, { useState } from 'react';

import { Formik, Form } from 'formik';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDAlert from 'components/MDAlert';

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from 'composites/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'composites/Navbars/DashboardNavbar';
import Footer from 'composites/Footer';

// NewUser page components
import UserInfo from './components/UserInfo';

// NewUser layout schemas for form and form feilds
import validations from './schemas/validations';
import form from './schemas/form';
import { useUser } from 'context/user';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createAccount, getUser, updateAccount } from 'api/account';
import { PRICE_PER_TOKEN } from 'config';

function NewUser(): JSX.Element {
  const { user, accessToken } = useUser();
  const {
    formId,
    formField,
    formField: {
      fullName,
      email,
      password,
      repeatPassword,
      isActive,
      subscriptionDuration,
      allowCustomPlaylists,
      playlist,
    },
  } = form;

  const [formValues, setFormValues] = useState(null);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');

  const fetchAndSetUser = async (emailParam: string, accessToken: string) => {
    const { account, user, error } = await getUser(emailParam, accessToken);

    setFormValues({
      fullName: user?.displayName,
      email: account?.email,
      isActive: account?.isActive,
      allowCustomPlaylists:
        account?.allowCustomPlaylists !== undefined
          ? account.allowCustomPlaylists
          : true,
      playlist: account?.playlist || '',
    });
  };

  React.useEffect(() => {
    if (emailParam && accessToken) {
      fetchAndSetUser(emailParam, accessToken);
    }
  }, [emailParam, accessToken]);

  const submitForm = async (values: any, actions: any) => {
    setError(null);
    // Calculate credit cost
    const subscriptionMonths = parseInt(values.subscriptionDuration);
    //@ts-ignore
    const priceModifier = user.priceModifiers[values.subscriptionDuration] ?? 1;
    const creditCost = subscriptionMonths * PRICE_PER_TOKEN * priceModifier;

    // Check if user has enough credits
    if (user.credits < creditCost) {
      setError('Insufficient credits for this subscription duration');
      actions.setSubmitting(false);
      return;
    }
    if (!values.allowCustomPlaylists && !values.playlist) {
      setError('Playlist is required if custom playlists are not allowed');
      actions.setSubmitting(false);
      return;
    }

    const payload = {
      fullName: values.fullName,
      email: values.email,
      password: values.password,
      isActive: values.isActive,
      accessToken: accessToken,
      subscriptionDuration: Number(values.subscriptionDuration),
      allowCustomPlaylists: values.allowCustomPlaylists,
      playlist: values.playlist,
    };

    const method = emailParam ? updateAccount : createAccount;

    const { error } = await method(payload);

    if (error) {
      setError(error);
      actions.setSubmitting(false);
      return;
    }

    navigate('/accounts');
  };

  const handleSubmit = (values: any, actions: any) => {
    submitForm(values, actions);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} mb={20} height="65vh">
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ height: '100%', mt: 8 }}
        >
          <Grid item xs={12} lg={8}>
            <Formik
              initialValues={{
                [fullName.name]: formValues?.fullName || '',
                [email.name]: formValues?.email || '',
                [password.name]: formValues?.password || '',
                [repeatPassword.name]: formValues?.repeatPassword || '',
                [isActive.name]: formValues?.isActive || true,
                [subscriptionDuration.name]:
                  formValues?.subscriptionDuration || '1',
                [allowCustomPlaylists.name]:
                  formValues?.allowCustomPlaylists ?? true,
                [playlist.name]: formValues?.playlist || '',
              }}
              validationSchema={validations}
              enableReinitialize={true}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting, handleChange }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: '100%' }}>
                    <MDBox p={3}>
                      <MDBox>
                        {error && (
                          <MDAlert color="error" mb={2}>
                            {error}
                          </MDAlert>
                        )}
                        <UserInfo
                          formData={{
                            values,
                            touched,
                            formField,
                            errors,
                            handleChange,
                            allowCustomPlaylists,
                            playlist,
                          }}
                        />
                        <MDBox
                          mt={2}
                          width="100%"
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            disabled={isSubmitting}
                            type="submit"
                            variant="gradient"
                            color="dark"
                          >
                            {emailParam ? 'Update' : 'Create'}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewUser;
